<script setup>

import { ref, reactive, toRefs, computed, watch, nextTick } from 'vue';
import InfoBoxBottom from '@/components/cloudCounter/InfoBoxBottom.vue';

const props = defineProps({
    beginDate: {
        type: String,
        default: '',
    },
    userDate: {
        type: String,
        default: '',
    },
    // userDate是民國年嗎
    isYearROC: {
        type: Boolean,
        default: false,
    },
    visiblePopup: {
        type: Boolean,
        default: true,
    },
    // 需要顯示今日按鈕嗎?
    hasTodayButton: {
        type: Boolean,
        default: false,
    },
    maxDate: {
        type: String,
        default: '',
    },
    minDate: {
        type: String,
        default: '',
    },
    boxTitle: {
        type: String,
        default: '請選擇日期',
    },
    okHandel: Function,
});
const emit = defineEmits(['update:userDate', 'update:visiblePopup', 'okHandel']);
const { userDate, isYearROC, maxDate, minDate, visiblePopup, beginDate } =
    toRefs(props);
const activeDate = ref('');
const templateList = reactive({
    d: 'day',
    m: 'month',
    y: 'year',
});
const templateShow = ref('day');
const selectedDateList = reactive({
    year: '',
    month: '',
    day: '',
});

// 取得被選擇到的日期
// const selectedDay = computed(() => {
//     const { year:y, month:m, day:d } = selectedDateList;
//     return `${y}/${padStart2(m + 1)}/${padStart2(d)}`;
// });

const weekList = ['日', '一', '二', '三', '四', '五', '六'];
const monthList = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];

// 小於10補0
const padStart2 = (num) => {
    return String(num).padStart(2, '0');
};

// 格式化日期
const formatDate = (d) => {
    return {
        y: !d ? new Date().getFullYear() : new Date(d).getFullYear(),
        m: !d ? new Date().getMonth() : new Date(d).getMonth(),
        d: !d ? new Date().getDate() : new Date(d).getDate(),
    };
};
// 民國年轉換成西元年
const changeROCtoAD = (d) => {
    const dateList = d.split('/');
    if (dateList[0].includes('元')) {
        dateList[0] = 1912;
    } else if (dateList[0].includes('前')) {
        const y = dateList[0].split('前');
        dateList[0] = 1912 - Number(y[1]);
    } else {
        dateList[0] = Number(dateList[0]) + 1911;
    }
    return dateList.join('/');
};

// 西元年轉民國年
const showROCtext = (year) => {
    const rocYear = year - 1911;
    if (rocYear === 1) {
        return '元';
    } else if (rocYear < 1) {
        return `前${rocYear * -1 + 1}`;
    } else {
        return rocYear;
    }
};

// 最大日期變成物件 {y:2022,m:12,d:1, millisecond: 毫秒, yearAtleast: 年列表的最大結束年}
let maxDateList = reactive({});
// 最小日期變成物件 {y:2022,m:12,d:1}
let minDateList = reactive({});

/**
 * 轉成毫秒
 * @param {String} 日期'2022/2/22'
 * @return {Number}  毫秒
 */
const toMillisecond = (d) => {
    return Date.parse(new Date(d));
};

/**
 * 驗證日期是否可以選擇，不能選擇的畫面disabled
 * @param {String} 依照templateShow.value可能傳入 日期: yyyy/mm/dd， 月: m ，年: y
 * @return {Boolean}  true=可以選之日期
 */
const betweenDay = (d) => {
    switch (templateShow.value) {
        case templateList.y: {
            return d <= maxDateList.y && d >= minDateList.y;
        }
        case templateList.m: {
            return (
                toMillisecond(`${selectedDateList.year}/${d + 1}/1`) <=
                maxDateList.millisecond &&
                toMillisecond(
                    `${selectedDateList.year}/${d + 1}/${monthDays.value}`
                ) >= minDateList.millisecond
            );
        }
        case templateList.d: {
            const dayMillisecond = toMillisecond(d);
            return (
                dayMillisecond <= maxDateList.millisecond &&
                dayMillisecond >= minDateList.millisecond
            );
        }
        default:
            return true;
    }
};

// 取得今日日曆日期
const today = computed(() => {
    const { y, m, d } = formatDate();
    return `${y}/${padStart2(m + 1)}/${padStart2(d)}`;
});

// 取得日曆日期，沒有值就取今日或是最靠近日
const userDateFormat = computed(() => {
    let list = {};
    if (userDate.value) {
        const d = isYearROC.value
            ? changeROCtoAD(userDate.value)
            : userDate.value;
        list = formatDate(d);
    } else if (beginDate.value) {
        const d = isYearROC.value
            ? changeROCtoAD(beginDate.value)
            : beginDate.value;
        list = formatDate(d);
    } else {
        list = formatDate('');
    }
    return list;
});

/**
 * 西元年/民國年的顯示方式
 * @return {Object} {AD西元日期/ROC民國日期}
 */
const selectDate = computed(() => {
    const { year, month, day } = selectedDateList;
    const mm = padStart2(month + 1);
    const dd = padStart2(day);
    return {
        AD: `${year}/${mm}/${dd}`,
        ROC: `${showROCtext(year)}/${mm}/${dd}`,
    };
});

// 當月有幾天
const monthDays = computed(() =>
    new Date(selectedDateList.year, selectedDateList.month + 1, 0).getDate()
);

/**
 * 日子: 顯示用的日曆日期
 * @return {Array.String}  ['y/m+1/d','y/m+1/d']
 */
const monthDayList = computed(() => {
    const days = [];
    const { year, month } = selectedDateList;
    const mm = padStart2(month + 1);
    for (let i = 1; i <= monthDays.value; i++) {
        days.push(`${year}/${mm}/${padStart2(i)}`);
    }
    const startWeekDay = new Date(year, month, 1).getDay();
    if (startWeekDay !== 0) {
        for (let i = 0; i <= startWeekDay - 1; i++) {
            days.unshift('');
        }
    }
    return days;
});

const containerElement = ref(null);
const yearElement = ref(null);

// 顯示選擇的年月日清單
const changeTemplate = (type) => {
    templateShow.value = type;
    if (type === 'year') {
        nextTick(() => {
            if (document.getElementById('yearActive')) {
                containerElement.value.scrollTop = document.getElementById('yearActive').offsetTop;
            }
            // if (yearElement.value) {
            //     containerElement.value.scrollTop = yearElement.value[selectedYearIndex.value].offsetTop;
            // }
        });
    }
};

// 設定選取日期
const setDate = (list) => {
    if (!list) {
        list = formatDate();
        changeTemplate(templateList.d);
    }
    selectedDateList.day = list.d;
    selectedDateList.month = list.m;
    selectedDateList.year = list.y;
};

const setDay = (d) => {
    selectedDateList.day = d;
    // emit(
    //     'update:userDate',
    //     isYearROC.value ? selectDate.value.ROC : selectDate.value.AD
    // );
    // handleClose();
};

const updateUserDate = async () => {
    emit(
        'update:userDate',
        isYearROC.value ? selectDate.value.ROC : selectDate.value.AD
    );
    if (props.okHandel) {
        await props.okHandel();
    }
    handleClose();
}


const setMonth = (m) => {
    selectedDateList.month = m;
    changeTemplate(templateList.d);
};

const setYear = (y) => {
    selectedDateList.year = y;
    changeTemplate(templateList.m);
};

// 年份清單
const yearList = computed(() => {
    // 製作清單
    const list = [];
    // const startYear =
    //     Math.floor((selectedDateList.year - 1 - 1911) / 16) * 16 + 1911;

    // for (let i = 1; i <= 16; i++) {
    //     list.push(startYear + i);
    // }

    for (let i = 0; i <= maxDateList.y - minDateList.y; i++) {
        list.push(minDateList.y + i);
    }

    // console.log(minDateList.y, maxDateList.y);

    return list || [];
});

const selectedYearIndex = computed(() => {
    let _index = 0;

    for (let i = 0; i < yearList.value.length; i++) {
        if (yearList.value[i] === selectedDateList.year) {
            _index = i;
            break;
        }
    }

    return _index;

});



// 上一個的年?月?日?
const back = () => {
    const { year, month } = selectedDateList;
    switch (templateShow.value) {
        case templateList.y: {
            if (containerElement.value.scrollTop > 0) {
                // containerElement.value.scrollTop -= (containerElement.value.clientHeight + 15);
                containerElement.value.scrollTo({top: (containerElement.value.scrollTop - containerElement.value.clientHeight + 15), behavior: 'smooth'});
            }
            // const yearLimit = Math.floor((year - 1911) / 16) * 16 + 1 + 1911;
            // if (yearLimit > minDateList.y) {
            //     selectedDateList.year -= 16;
            // }
            break;
        }
        case templateList.m: {
            if (year > minDateList.y) {
                selectedDateList.year -= 1;
            }
            break;
        }
        case templateList.d: {
            const dayMillisecond = toMillisecond(`${year}/${month + 1}/1`);
            if (
                dayMillisecond <= maxDateList.millisecond &&
                dayMillisecond >= minDateList.millisecond
            ) {
                selectedDateList.month = month === 0 ? 11 : month - 1;
                if (selectedDateList.month === 11) {
                    selectedDateList.year -= 1;
                }
            }
            break;
        }
    }
};

// 下一個的年?月?日?
const next = () => {
    const { year, month } = selectedDateList;
    switch (templateShow.value) {
        case templateList.y: {
            if (containerElement.value.scrollTop < (containerElement.value.scrollHeight - containerElement.value.clientHeight)) {
                // containerElement.value.scrollTop += (containerElement.value.clientHeight + 15);
                containerElement.value.scrollTo({top: (containerElement.value.scrollTop + containerElement.value.clientHeight + 15), behavior: 'smooth'});
            }
            // if (year + 20 < maxDateList.yearListEnd) {
            //     selectedDateList.year += 20;
            // }
            break;
        }
        case templateList.m: {
            if (year <= maxDateList.y) {
                selectedDateList.year += 1;
            }
            break;
        }
        case templateList.d: {
            const dayMillisecond = toMillisecond(
                `${year}/${month + 1}/${monthDays.value}`
            );
            if (
                dayMillisecond <= maxDateList.millisecond &&
                dayMillisecond >= minDateList.millisecond
            ) {
                selectedDateList.month = month === 11 ? 0 : month + 1;
                if (selectedDateList.month === 0) {
                    selectedDateList.year += 1;
                }
            }
            break;
        }
    }
};

const handleClose = () => {
    emit('update:visiblePopup', false);
};

watch(
    () => visiblePopup.value,
    (nV) => {
        if (nV) {
            changeTemplate(templateList.d);
            setDate(userDateFormat.value);
            activeDate.value = selectDate.value.AD;
        }
    },
    { immediate: true }
);

// 最小日期
watch(
    () => minDate.value,
    (nV) => {
        let minDateAD = '';
        if (nV) {
            minDateAD = isYearROC.value ? changeROCtoAD(nV) : nV;
        } else {
            const { y, m, d } = formatDate();
            minDateAD = `${y - 110}/${m + 1}/${d}`;
        }
        minDateList = {
            ...formatDate(minDateAD),
            millisecond: toMillisecond(minDateAD),
        };
    },
    { immediate: true }
);

// 最大日期
watch(
    () => maxDate.value,
    (nV) => {
        let maxDateAD = '';
        if (nV) {
            maxDateAD = isYearROC.value ? changeROCtoAD(nV) : nV;
        } else {
            const { y, m, d } = formatDate();
            maxDateAD = `${y }/${m+1 }/${d }`;
        }
        maxDateList = {
            ...formatDate(maxDateAD),
            millisecond: toMillisecond(maxDateAD),
        };
        maxDateList.yearListEnd =
            Math.ceil((maxDateList.y - 1911) / 20) * 20 + 1911;
    },
    { immediate: true }
);
</script>

<template>
    <InfoBoxBottom :boxTitle="boxTitle" :closeHandle="() => { handleClose(); }" contentClass="p-0" v-if="visiblePopup"
        :visible="visiblePopup">
        <div class="mx-auto card w-full bg-white pt-4">
            <div class="min-w-full overflow-auto ">

                <div class="flex justify-between items-center">
                    <!-- 左箭頭 -->
                    <div>
                        <button class="h-full border-0 px-6 py-4" @click="back">
                            <svg width="9" height="16" viewBox="0 0 9 16" fill="none"
                                xmlns="http://www.w3.org/2000/svg">
                                <path d="M8 1L1 8L8 15" stroke="#0093C1" stroke-width="2" stroke-linecap="round"
                                    stroke-linejoin="round" />
                            </svg>
                        </button>
                    </div>
                    <h3 class="border-0 bg-white text-center font-bold text-gray-1e ">
                        <button class="mr-4" @click="(e) => {
        (templateShow === templateList.d) ? changeTemplate(templateList.m) : changeTemplate(templateList.y);
    }" :class="{ 'text-[#0093C1]': templateShow === 'year', 'text-[#86878F]': templateShow === 'month' }">
                            {{ (isYearROC ? `民國 ${showROCtext(selectedDateList.year)}` : `${selectedDateList.year} `) +
        ` 年`
                            }}
                        </button>
                        <button @click="(e) => { changeTemplate(templateList.m); }"
                            :class="{ 'text-[#0093C1]': templateShow === 'month', 'text-[#86878F]': templateShow === 'year' }">
                            {{ `${selectedDateList.month + 1} 月` }}
                        </button>
                    </h3>
                    <!-- 右箭頭 -->
                    <div>
                        <button class="h-full border-0 px-6 py-4" @click="next">
                            <svg width="9" height="16" viewBox="0 0 9 16" fill="none"
                                xmlns="http://www.w3.org/2000/svg">
                                <path d="M1 15L8 8L1 0.999999" stroke="#0093C1" stroke-width="2" stroke-linecap="round"
                                    stroke-linejoin="round" />
                            </svg>
                        </button>
                    </div>
                </div>

                <ul class="grid auto-cols-[2.5rem] auto-rows-[2.5rem] grid-cols-7 gap-x-0 gap-y-2"
                    :class="{ 'invisible': templateShow !== templateList.d }">
                    <li v-for="(weekday, index) in weekList" :key="index"
                        class="flex items-center justify-center  bg-[#EEF0F0]">
                        {{ weekday }}
                    </li>
                </ul>
                <div class="relative h-[300px] overflow-y-auto" ref="containerElement">
                    <!-- 日曆列表 -->
                    <template v-if="templateShow === templateList.d">
                        <ul class="pt-3 grid auto-cols-[2.5rem] auto-rows-[2.5rem] grid-cols-7 gap-x-0 gap-y-2">
                            <li v-for="(item, i) in monthDayList" :key="i"
                                class="flex items-center justify-center py-2">
                                <button class="h-11 w-11 rounded-full font-bold relative" :class="[
        // item && betweenDay(item) && 'cursor-pointer hover:bg-blue-100',
        today === item && selectDate.AD !== item && `text-[#0093C1] after:absolute after:-bottom-1 after:left-1/2 after:-translate-x-1/2 after:block after:content-['•']`,
        item && !betweenDay(item) && 'bg-transparent text-[#bbb]',
        selectDate.AD === item && 'bg-[#0093C1] text-white',
    ]" @click="betweenDay(item) ? setDay(new Date(item).getDate()) : ''">
                                    {{ item ? new Date(item).getDate() : '' }}
                                </button>
                            </li>
                        </ul>
                    </template>
                    <!-- 月份列表 -->
                    <template v-if="templateShow === templateList.m">
                        <ul class="grid min-h-[14rem] auto-rows-[2.5rem] grid-cols-3 grid-rows-4 gap-1">
                            <li v-for="(month, index) in monthList" :key="index"
                                class="flex h-full justify-center items-center">
                                <button class="min-w-[44px] h-[44px] rounded-full border-0 font-bold relative" :class="[
        !betweenDay(month) && 'text-[#bbb]',
        // betweenDay(month) &&
        // 'text-gray-1e hover:bg-blue-100',
        userDateFormat.y ===
        selectedDateList.year &&
        userDateFormat.m === month && selectedDateList.month !== month &&
        `text-[#0093C1] after:absolute after:-bottom-1 after:left-1/2 after:-translate-x-1/2 after:block after:content-['•']`,
        selectedDateList.month === month && 'bg-[#0093C1] text-white',
    ]" :disabled="!betweenDay(month)" @click="
        betweenDay(month) ? setMonth(month) : ''
        ">
                                    {{ month + 1 }} 月
                                </button>
                            </li>
                        </ul>
                    </template>
                    <!-- 年份列表 -->
                    <template v-else-if="templateShow === templateList.y">

                        <ul class="grid min-h-[14rem] auto-rows-[3rem] grid-cols-4 gap-1">
                            <li v-for="(year, index) in yearList" :key="index" ref="yearElement" :id="(selectedDateList.year === year) && 'yearActive'"
                                class="flex h-full w-full items-center justify-center">
                                <button
                                    class="w-auto  min-w-[44px] h-[44px] rounded-full border-0 px-2 py-0 font-bold relative"
                                    :class="[
        !betweenDay(year) && 'text-[#bbb]',
        // betweenDay(year) && 'text-gray-1e hover:bg-blue-100',
        userDateFormat.y === year && selectedDateList.year !== year && `text-[#0093C1] after:absolute after:-bottom-1 after:left-1/2 after:-translate-x-1/2 after:block after:content-['•']`,
        selectedDateList.year === year && 'bg-[#0093C1] text-white',
    ]" :disabled="!betweenDay(year)" @click="betweenDay(year) ? setYear(year) : ''">
                                    {{ isYearROC ? showROCtext(year) : year }} 年
                                </button>
                            </li>
                        </ul>
                    </template>
                </div>
                <div class="mt-2.5 flex items-baseline justify-between p-4">
                    <div>
                        <button v-if="hasTodayButton" class="text-left text-sm font-bold" @click="setDate()"
                            v-text="'今日'" />
                    </div>
                    <button class="w-full text-white bg-[#0093C1] py-4 rounded-md"
                        @click="(e) => { updateUserDate() }">確定</button>
                </div>
            </div>
        </div>
    </InfoBoxBottom>
</template>
